import { makeAutoObservable, runInAction } from "mobx";
import RootStore from "../../../stores/RootStore";

interface Job {
    id: string;
    jobNumber: string;
    projectName: string;
    client: string;
    totalPanels: number;
    panelsDesigned: number;
    panelsInProduction: number;
    panelsCompleted: number;
    status: string;
    projectManager: string;
    targetCompletion: string;
}

interface Panel {
    id: string;
    panelNumber: string;
    panelType: string;
    dimensions: string;
    designStatus: string;
    designer: string;
    productionStatus: string;
    priority: string;
    scheduledDate: string;
}

export default class JobPanelManagementStore {
    // Observables
    isLoading = false;
    error: string | null = null;
    jobs: Job[] = [];
    currentJob: Job | null = null;
    panels: Panel[] = [];
    statusFilter = "all";

    // Constructor
    constructor(private rootStore: RootStore) {
        makeAutoObservable(this);
    }

    // Actions
    setLoading(loading: boolean) {
        this.isLoading = loading;
    }

    setError(error: string | null) {
        this.error = error;
    }

    setStatusFilter(filter: string) {
        this.statusFilter = filter;
    }

    // Async actions
    async loadAllJobs() {
        try {
            this.setLoading(true);
            this.setError(null);

            // Mock data for now
            const mockJobs: Job[] = [
                {
                    id: "job-1",
                    jobNumber: "JOB-2024-001",
                    projectName: "Office Building A",
                    client: "ABC Construction",
                    totalPanels: 45,
                    panelsDesigned: 30,
                    panelsInProduction: 10,
                    panelsCompleted: 5,
                    status: "In Progress",
                    projectManager: "John Smith",
                    targetCompletion: "2024-08-15",
                },
                {
                    id: "job-2",
                    jobNumber: "JOB-2024-002",
                    projectName: "Residential Complex B",
                    client: "XYZ Developers",
                    totalPanels: 120,
                    panelsDesigned: 80,
                    panelsInProduction: 25,
                    panelsCompleted: 15,
                    status: "In Progress",
                    projectManager: "Jane Doe",
                    targetCompletion: "2024-09-30",
                },
            ];

            runInAction(() => {
                this.jobs = mockJobs;
            });
        } catch (error: any) {
            runInAction(() => {
                this.setError(error.message || "Failed to load jobs");
            });
            console.error("Failed to load jobs:", error);
        } finally {
            this.setLoading(false);
        }
    }

    async loadJobPanels(jobId: string) {
        try {
            this.setLoading(true);
            this.setError(null);

            // Mock data for now
            const mockJob: Job = {
                id: jobId,
                jobNumber: "JOB-2024-001",
                projectName: "Office Building A",
                client: "ABC Construction",
                totalPanels: 45,
                panelsDesigned: 30,
                panelsInProduction: 10,
                panelsCompleted: 5,
                status: "In Progress",
                projectManager: "John Smith",
                targetCompletion: "2024-08-15",
            };

            const mockPanels: Panel[] = [
                {
                    id: "panel-1",
                    panelNumber: "P-001",
                    panelType: "Wall Panel",
                    dimensions: "8' x 10'",
                    designStatus: "Completed",
                    designer: "Alice Johnson",
                    productionStatus: "Ready for Production",
                    priority: "High",
                    scheduledDate: "2024-07-15",
                },
                {
                    id: "panel-2",
                    panelNumber: "P-002",
                    panelType: "Floor Panel",
                    dimensions: "12' x 8'",
                    designStatus: "In Progress",
                    designer: "Bob Wilson",
                    productionStatus: "Design Pending",
                    priority: "Medium",
                    scheduledDate: "2024-07-20",
                },
            ];

            runInAction(() => {
                this.currentJob = mockJob;
                this.panels = mockPanels;
            });
        } catch (error: any) {
            runInAction(() => {
                this.setError(error.message || "Failed to load job panels");
            });
            console.error("Failed to load job panels:", error);
        } finally {
            this.setLoading(false);
        }
    }

    // Computed values
    get filteredPanels() {
        if (this.statusFilter === "all") {
            return this.panels;
        }
        return this.panels.filter(panel =>
            panel.designStatus.toLowerCase().includes(this.statusFilter.toLowerCase()) ||
            panel.productionStatus.toLowerCase().includes(this.statusFilter.toLowerCase())
        );
    }

    get hasJobs() {
        return this.jobs.length > 0;
    }
}
